import * as React from "react";
import { createContext, useState, useEffect, useContext } from "react";
import { IFormThemeValues } from "./pages/config/BrandingPage";
import { Cache } from "./Account";
import { axios } from "./AxiosInstanceManager";

export interface LocationConfigurationAndHashCode {
  locationConfiguration: LocationConfiguration;
  hashCode: number;
}

export const LocationConfigurationHolder = (() => {
  // TODO use WindowVariables?
  let config: string = window.config || "pac";
  const requestedHashCodes: Map<number, boolean> = new Map();
  let hashCode: number = 0;
  let locationConfiguration = new Cache<LocationConfiguration>(
    null,
    4 * 60 * 1000
  );
  const subscribers: ((event: LocationConfiguration) => void)[] = [];

  let timeoutId: number;

  let pendingGetLocationConfiguration: Promise<LocationConfiguration>;

  const setLocationConfiguration = ({
    locationConfiguration: newLocationConfiguration,
    hashCode: newHashCode,
  }: LocationConfigurationAndHashCode) => {
    hashCode = newHashCode;
    if (newLocationConfiguration) {
      // will be null if it hasn't changed
      locationConfiguration.set(newLocationConfiguration);
      subscribers.forEach((subscriber) =>
        subscriber({ ...newLocationConfiguration })
      );
    }
  };

  // TODO: Store the pending promise so that we can share the network request across multiple concurrent front-end request
  const fetchLocationConfiguration = async () => {
    const hc = hashCode || 0;
    // cancel existing timeout
    const updateFrequency = 30 * 1000; //30 seconds

    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }
    timeoutId = window.setTimeout(() => {
      fetchLocationConfiguration();
    }, updateFrequency);

    if (pendingGetLocationConfiguration) {
      return pendingGetLocationConfiguration; //return the promise
    }

    if (!locationConfiguration.valid) {
      pendingGetLocationConfiguration = axios
        .post<LocationConfigurationAndHashCode>(
          "/locationConfiguration/getIfChanged",
          {
            config,
            hc,
          }
        )
        .then(({ data }) => {
          setLocationConfiguration(data);
          return locationConfiguration.current;
        })
        .finally(() => {
          pendingGetLocationConfiguration = null;
        });
      return pendingGetLocationConfiguration;
    }

    return locationConfiguration.current;
  };

  const saveConfig = async (locationConfig: LocationConfiguration) => {
    try {
      const { data } = await axios.put<LocationConfigurationManagerStatus[]>(
        `/admin/locationConfiguration/${locationConfig.id}`,
        locationConfig
      );

      // There should only be one LocationConfigurationManagerStatus
      const response = data[0];
      if (response.success === true) {
        locationConfiguration.invalidate();
        await fetchLocationConfiguration(); //this is here so that any subscribers get updated with a notifaction
        return true;
      }
      console.error(response.message);
      return false;
    } catch (e) {
      console.log(e);
      return false;
    }
  };

  const saveTheme = async ({
    primaryColor,
    secondaryColor,
    appBarColor,
    mainPageLogoImageId,
    mainPageLogoImageAltText,
    libraryName,
    headerPosition, 
    showHeaderOnMobile 
  }: IFormThemeValues) => {
    // Because save config expect an admin view of the location configuration we need to request it
    const adminLocationConfigurationData = (
      await axios.get<LocationConfiguration>(
        `/admin/locationConfiguration/${locationConfiguration.current.id}`
      )
    ).data;
    const displaySettings: DisplaySettings = {
      ...adminLocationConfigurationData.pacSettings.displaySettings,
      primaryColor,
      secondaryColor,
      appBarColor,
      mainPageLogoImageId,
      mainPageLogoImageAltText,
      headerPosition, 
      showHeaderOnMobile
    };

    const pacSettings = {
      ...adminLocationConfigurationData.pacSettings,
      displaySettings,
    };
    const payload = { ...adminLocationConfigurationData, libraryName, pacSettings };
    // await LocationConfigurationHolder.saveConfig(payload);
    return await saveConfig(payload);
  };

  const subscribe = (
    subscriber: (locationConfiguration: LocationConfiguration) => void
  ) => {
    // Add the subscriber function to the list of subscribers
    subscribers.push(subscriber);
    fetchLocationConfiguration();

    if (locationConfiguration.valid) {
      subscriber(locationConfiguration.current);
    }

    // Return an unsubscribe function
    return () => {
      const index = subscribers.indexOf(subscriber);
      if (index !== -1) {
        subscribers.splice(index, 1); // Remove the subscriber when unsubscribed
      }
    };
  };

  const refresh = () => {
    locationConfiguration.invalidate();
    fetchLocationConfiguration();
  };

  return {
    subscribe,
    refresh,
    saveTheme,
    fetchLocationConfiguration,
    getLocationConfiguration: () => {
      return locationConfiguration.current;
    },
  };
})();

export interface LocationConfiguration {
  id: number;
  name: string;
  isDefault: boolean;
  libraryName: string;
  helpSettings: HelpSettings;
  pacSettings: PacSettings;
  filterBranches: Branch[];
}

export interface Branch {
  id: number;
  displayName: string;
  longDescription: string;
  identifier: string;
  shared: boolean;
  displayInCopies: boolean;
  displayInBranchLimits: boolean;
  displayInPickupLocation: boolean;
}

export interface HelpSettings {
  helpURLBase: string;
  localHelpURL: string;
}

export interface PacSettings {
  id: number;
  noveListSettings: NoveListSettings;
  googleAnalyticsSettings: GoogleAnalyticsSettings;
  displaySettings: DisplaySettings;
  userActionSettings: UserActionSettings;
  bookingsSettings: BookingsSettings;
  facetDisplayInformations: FacetDisplayInformation[];
  materialDescriptionViewConfigItems: MaterialDescriptionViewConfigItem[];
  useAlternateTimeoutPage: boolean;
  alternateTimeoutPageUrl: string;
  alternateLinkURL: string;
  alternateLinkText: string;
  googleSSOEnabled: boolean;
  obrEnabled: boolean;
  reservesEnabled: boolean;
  overrideDownloadNowStationLocation: boolean;
  acceleratedReaderEnabled: boolean;
  acceleratedReaderShowQuizNumber: boolean;
  lexileEnabled: boolean;
  additionalLink1: AdditionalLink;
  additionalLink2: AdditionalLink;
  additionalLink3: AdditionalLink;
  additionalLink4: AdditionalLink;
  additionalLink5: AdditionalLink;
  syndeticsUnboundInstanceId: string;
  syndeticsConsortiaIndexing: boolean;
  kidsSettings: any;
  ecommerceEnabled: boolean;
  useOverDrive: boolean;
  worldBookDbOptions: WorldBookDbOption[];
  useEbsco: boolean;
  useNoveList: boolean;
  useBritannica: boolean;
  useNewsBank: boolean;
  useGale: boolean;
  useBakerAndTaylor: boolean;
  useMackin: boolean;
  useTeachingBooks: boolean;
  useWorldBook: boolean;
  stackMapSettings: StackMapSettings;
}

export interface StackMapSettings {
  stackMapEnabled: boolean;
  stackMapUrl: string;
}

export interface NoveListSettings {
  useNoveList: boolean;
  noveListAccount: string;
  noveListPassword: string;
}

export interface GoogleAnalyticsSettings {
  googleTrackingCode: string;
  googleTrackingEnabled: boolean;
  googleQuirkMode: boolean;
}

export enum AppBarColor {
  Default = "Default",
  Primary = "Primary",
  Secondary = "Secondary",
}

export enum HeaderPosition {
  Top = "Top",
  Bottom = "Bottom"
}

export interface DisplaySettings {
  headerPosition: HeaderPosition;
	showHeaderOnMobile: boolean;
  appBarColor: AppBarColor;
  defaultPrimaryColor: string;
  defaultSecondaryColor: string;
  primaryColor: string;
  secondaryColor: string;
  showResetPINLink: boolean;
  displayGuardianLogin: boolean;
  showDisplayCase: boolean;
  showSmartGraphic: boolean;
  showUserReviewsView: boolean;
  showMarcRecordView: boolean;
  showBrowse: boolean;
  showMapView: boolean;
  showTotalCheckouts: boolean;
  showTotalHolds: boolean;
  showDependentBorrowers: boolean;
  useDummyJackets: boolean;
  displayNYTimesInDisplayCase: boolean;
  displayNewTitlesInDisplayCase: boolean;
  displayCopiesFirst: boolean;
  useAlternateBookRiver: boolean;
  voiceInterface: boolean;
  showRememberMe: boolean;
  displayAudienceCharacteristicsFilter: boolean;
  openLinksInNewTab: boolean;
  displayVirtualShelf: boolean;
  usePrefixInVirtualShelf: boolean;
  displayReadingLevelInSearchResults: boolean;
  displaySelectPickupLocation: boolean;
  smartGraphicLinkUrl: any;
  bookRiverLogoImageId: any;
  mainPageLogoImageId: any;
  mainPageLogoImageAltText: string;
}

export interface UserActionSettings {
  allowTagging: boolean;
  allowTitleLevelHolds: boolean;
  restrictTitleLevelHolds: boolean;
  allowItemLevelHolds: boolean;
  allowOnShelfHolds: boolean;
  allowBibExport: boolean;
  allowRenewals: boolean;
  useUsersBranchForAvailability: boolean;
}

export interface BookingsSettings {
  allowBookings: boolean;
  bookingsBranchIdentifier: string;
}

export interface FacetDisplayInformation {
  name: string;
  displayName: string;
  displayCount: number;
  isImageMapped: boolean;
  luceneFacetName: string;
  displayed: boolean;
}

export interface MaterialDescriptionViewConfigItem {
  id: number;
  label: string;
  tagLocation: boolean;
  fields: Field[];
}

export interface Field {
  id: number;
  tag: number;
  subfields: string;
}

export interface AdditionalLink {
  additionalLinkURL: string;
  additionalLinkText: string;
}

export interface AdditionalLink2 {
  additionalLinkURL: string;
  additionalLinkText: string;
}

export interface WorldBookDbOption {
  name: string;
  value: string;
  useThis: boolean;
  defaultDb: boolean;
}

interface LocationConfigurationContextType {
  locationConfiguration: LocationConfiguration;
}

interface ConfigurationGroup {
  id: number;
  name: string;
  isUserDefined: boolean;
  locationConfigurationIds: number[];
}

interface LocationConfigurationInfo {
  id: number;
  libraryName: string;
  name: string;
  isDefault: boolean;
  configurationGroup: ConfigurationGroup[];
  filterBranches: Branch[];
  updateCollectionFilters: boolean;
}

interface LocationConfigurationManagerStatus {
  success: boolean;
  message: string;
  locationConfigurationInfo: LocationConfigurationInfo;
}

export const LocationConfigurationContext =
  createContext<LocationConfigurationContextType>(null);

export const LocationConfigurationProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [locationConfiguration, setLocationConfiguration] =
    useState<LocationConfiguration>(
      LocationConfigurationHolder.getLocationConfiguration()
    );

  useEffect(() => {
    const unsubscribe = LocationConfigurationHolder.subscribe((data) => {
      setLocationConfiguration(data);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const contextValue = {
    locationConfiguration,
  };

  return (
    <LocationConfigurationContext.Provider value={contextValue}>
      {children}
    </LocationConfigurationContext.Provider>
  );
};

export function useLocationConfiguration() {
  const context = useContext(LocationConfigurationContext);

  if (!context) {
    throw new Error(
      "useLocationConfiguration must be used within a LocationConfigurationProvider"
    );
  }

  return context;
}
